import React from 'preact/compat';

import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

import colors from '../colors';
import InfoIcon from '../icons/InfoIcon';
import LauncherPopup from './LauncherPopup';

const LauncherInfoPopup = ({open, onClose}) => {
    return (
        <LauncherPopup title="GDK Launcher Info & Help" open={open} onClose={onClose}
            Icon={InfoIcon} iconColor={colors.option}
        >
            <LauncherInformation />
        </LauncherPopup>
    );
};

const LauncherInformation = () => {
    return (
        <Box
            sx={{
                '& h3': {
                    fontSize: '24px',
                    fontWeight: 600,
                    marginBottom: '24px',
                    marginTop: '10px',
                    color: colors.primaryText
                },
                '& p, & ol': {
                    textAlign: 'left',
                    fontSize: '14px',
                    color: colors.secondaryText,
                    fontWeight: 400,
                    paddingBottom: '10px'
                },
                '& a': {
                    color: colors.option,
                    textDecoration: 'underline'
                },
                '& .reference': {
                    backgroundColor: colors.featureBackground,
                    color: colors.featureText,
                    padding: '3px 4px 1px',
                    borderRadius: '4px',
                    fontWeight: 600
                }
            }}
        >
            <SectionTitle title="What is the GDK Launcher" />
            <Typography>
                The GDK Launcher creates GDK <i>application templates</i>.
            </Typography>

            <SectionTitle title="How to use the GDK Launcher" />
            <Typography>
                Select the configuration for your application, then
                click <span className="reference">Generate Project</span> to
                generate and download an application template (a ZIP file).
            </Typography>
            <Typography>
                Uncompress the file in your preferred directory.
                The template contains configuration files that match your selection and includes necessary dependencies.
                Open it using your favourite IDE.
            </Typography>
            <Typography>
                The structure of the template is as follows:
                <ul>
                    <li>
                        a directory for each cloud platform you selected,
                    </li>
                    <li>
                        an application directory that contains artefacts (such as source code and configuration files) that are common to all cloud platforms.
                    </li>
                </ul>
            </Typography>

            <SectionTitle title="Available Options" />
            <Typography>
                The <Link href="/gdk/guides/" target="_blank"> GDK guides </Link> provide detailed descriptions of supported services.
            </Typography>
            <Typography>
                If you want to choose your own configuration:
                <ol>
                    <li>
                        Select your <span className="reference">Project Type</span> from the drop-down list.
                        For a server application, choose <i>Application</i>; for a serverless application,
                        choose <i>Function</i> or <i>Gateway Function</i>.
                        Enter a <i>Project Name</i> and a <i>Base Package</i> (or accept the defaults).
                    </li>
                    <li>
                        Select your cloud platform(s) from the <span className="reference">Clouds</span> check boxes.
                    </li>
                    <li>
                        Select a <i>Language</i>, <i>Build Tool</i>, <i>Test Framework</i>, and <i>Java Version</i> from
                        the corresponding radio buttons (or accept the defaults).
                    </li>
                    <li>
                        Select the services required by your application from the <span className="reference">Services</span> tab.
                    </li>
                    <li>
                        Select the Micronaut features required by your application from the <span className="reference">Features</span> tab.
                        (Use the search field to filter features.) For more information about Micronaut Features,
                        see <Link href="https://guides.micronaut.io/latest/" target="_blank">Micronaut Guides</Link>.
                    </li>
                    <li>
                        To ensure you have selected the services and features required by your application,
                        review the contents of the <span className="reference">Selected</span> tab.
                    </li>
                </ol>
            </Typography>
        </Box>
    );
}

const SectionTitle = ({title}) => {
    return (
        <Typography
            align="left"
            variant="h3"
        >
            {title}
            <Divider
                sx={{
                    marginTop: '4px',
                    borderColor: colors.inputBorder,
                    borderBottomWidth: 2
                }}
            />
        </Typography>
    );
}

export default LauncherInfoPopup;
